<!-- Australia lights up. The shared national display: every finished round
     anywhere adds one light, and towns switch on as the total passes each
     milestone. Never shows a number it did not fetch. -->
<template>
  <IonPage>
    <AppHeader title="Australia lights up" back="/" />

    <IonContent :fullscreen="true">
      <IonRefresher slot="fixed" @ionRefresh="onRefresh">
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>

      <div class="soc-hero australia-hero" aria-hidden="true">
        <canvas ref="mapEl" />
      </div>

      <div class="soc-stack australia-stack">
        <div class="australia-total">
          <p class="display-number tnum">{{ formatTotal(total) }}</p>
          <p class="soc-muted">lights across Australia</p>
          <p v-if="offline" class="soc-muted australia-offline">Showing the last total we saw{{ agoLine ? `, ${agoLine}` : '' }}. We'll update it when you're back online.</p>
        </div>

        <div v-if="total !== null" class="soc-card">
          <h2 class="section-title first">{{ milestoneHeading }}</h2>
          <div class="progress-bar" role="progressbar" :aria-valuenow="Math.round(milestone.progress * 100)" aria-valuemin="0" aria-valuemax="100">
            <div :style="{ width: `${Math.round(milestone.progress * 100)}%` }"></div>
          </div>
          <p class="soc-muted milestone-next">{{ milestoneSubline }}</p>
        </div>

        <div v-if="total !== null" class="soc-card">
          <h2 class="section-title first">Milestones</h2>
          <ul class="milestone-list">
            <li v-for="m in MILESTONES" :key="m" :class="{ 'milestone--reached': total >= m }">
              <span>{{ formatTotal(m) }} lights</span>
              <span class="milestone-state">{{ total >= m ? 'Reached' : 'Ahead' }}</span>
            </li>
          </ul>
        </div>

        <p class="soc-muted ion-text-center">Every finished round anywhere in Australia adds one light.</p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { IonPage, IonContent, IonRefresher, IonRefresherContent, onIonViewWillEnter, onIonViewDidLeave } from '@ionic/vue';
import { AppHeader } from '../core/ui';
import { national, settings, setNational, formatTotal, reducedMotion } from '../store.js';
import { fetchTotal, cachedTotal, milestoneFor, MILESTONES } from '../lib/national.js';
import { createRenderer } from '../lib/renderer.js';

const mapEl = ref(null);
const offline = ref(false);

const total = computed(() => (national.value && Number.isFinite(national.value.total) ? national.value.total : null));
const milestone = computed(() => milestoneFor(total.value ?? 0));

const agoLine = computed(() => {
  const ts = national.value?.fetchedAt;
  if (!ts) return '';
  const mins = Math.round(Math.max(0, Date.now() - ts) / 60000);
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
});

const milestoneHeading = computed(() => {
  const m = milestone.value;
  if (m.reached === null && m.next === null) return 'Getting started';
  if (m.next === null) return 'Every milestone reached';
  return `${formatTotal(m.next)} lights next`;
});

const milestoneSubline = computed(() => {
  const m = milestone.value;
  if (m.next === null) return 'The whole map is lit.';
  const remaining = Math.max(0, m.next - (total.value ?? 0));
  return `${formatTotal(remaining)} lights to go.`;
});

async function refresh() {
  const startedAt = Date.now();
  const entry = await fetchTotal();
  if (entry && entry.fetchedAt >= startedAt) {
    setNational(entry);
    offline.value = false;
  } else if (entry) {
    setNational(entry);
    offline.value = true;
  } else {
    offline.value = true;
    const cached = cachedTotal();
    if (cached) setNational(cached);
  }
}

async function onRefresh(ev) {
  await refresh();
  ev.target?.complete?.();
}

// --- the map scene ----------------------------------------------------------
let renderer = null;
let raf = 0;
let ro = null;

function loop(t) {
  if (renderer) renderer.drawAustralia(total.value ?? 0, milestone.value, t);
  if (reducedMotion()) { raf = 0; setTimeout(() => { if (renderer) raf = requestAnimationFrame(loop); }, 1000); return; }
  raf = requestAnimationFrame(loop);
}

function startMap() {
  if (!mapEl.value) return;
  if (!renderer) {
    renderer = createRenderer(mapEl.value, { highContrast: settings.highContrast, reducedMotion: reducedMotion() });
    ro = new ResizeObserver(() => renderer?.resize());
    ro.observe(mapEl.value);
  }
  renderer.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() });
  renderer.resize();
  if (!raf) raf = requestAnimationFrame(loop);
}

function stopMap() {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
}

watch(() => [settings.highContrast, settings.reducedMotion], () => renderer?.setOptions({ highContrast: settings.highContrast, reducedMotion: reducedMotion() }));

onMounted(() => {
  startMap();
  refresh();
});
onIonViewWillEnter(startMap);
onIonViewDidLeave(stopMap);
onBeforeUnmount(() => {
  stopMap();
  ro?.disconnect();
  renderer?.destroy();
  renderer = null;
});
</script>

<style scoped>
.australia-hero {
  aspect-ratio: 4 / 3;
  max-width: 34rem;
  margin: 0 auto;
}

.australia-stack {
  padding-top: var(--sp-4);
}

.australia-total {
  text-align: center;
}

.australia-total p {
  margin: 0;
}

.australia-offline {
  margin-top: var(--sp-2);
}

.section-title.first {
  margin-top: 0;
}

.milestone-next {
  margin: var(--sp-2) 0 0;
}

.milestone-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

.milestone-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--sp-2) 0;
  border-bottom: 1px solid var(--hairline);
  color: var(--dim);
}

.milestone-list li:last-child {
  border-bottom: none;
}

.milestone-state {
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  font-weight: 700;
}

.milestone--reached {
  color: var(--ink);
}

.milestone--reached .milestone-state {
  color: var(--soc-star);
}
</style>
